export interface QuoteCardData {
  content: string;
  childName: string;
  date: string;
  context?: string;
}

export interface QuoteCardTheme {
  id: string;
  name: string;
  background: string;
  textColor: string;
  accentColor: string;
  subtextColor: string;
}

export const QUOTE_CARD_THEMES: QuoteCardTheme[] = [
  {
    id: 'sage',
    name: 'Sage',
    background: '#8B9A8C',
    textColor: '#FFFFFF',
    accentColor: '#D4DDD5',
    subtextColor: '#E8EDE8',
  },
  {
    id: 'forest',
    name: 'Forest',
    background: '#2D3A2E',
    textColor: '#FFFFFF',
    accentColor: '#8B9A8C',
    subtextColor: '#C4CEC5',
  },
  {
    id: 'warm',
    name: 'Warm',
    background: '#E9D8BE',
    textColor: '#2D3A2E',
    accentColor: '#8B9A8C',
    subtextColor: '#5A6B5B',
  },
  {
    id: 'cream',
    name: 'Cream',
    background: '#FFF8F0',
    textColor: '#2D3A2E',
    accentColor: '#8B9A8C',
    subtextColor: '#7A8B7B',
  },
  {
    id: 'blush',
    name: 'Blush',
    background: '#F2E0D9',
    textColor: '#3D2C2C',
    accentColor: '#C49A8A',
    subtextColor: '#6B5050',
  },
  {
    id: 'sky',
    name: 'Sky',
    background: '#D6E5F0',
    textColor: '#1E3A4F',
    accentColor: '#7BA7C9',
    subtextColor: '#3D6585',
  },
];

type CardFormat = 'square' | 'story';

const CARD_SIZES: Record<CardFormat, { width: number; height: number }> = {
  square: { width: 1080, height: 1080 },
  story: { width: 1080, height: 1920 },
};

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export function renderQuoteCard(
  canvas: HTMLCanvasElement,
  data: QuoteCardData,
  theme: QuoteCardTheme,
  format: CardFormat = 'square'
): void {
  const { width, height } = CARD_SIZES[format];
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const padding = 100;
  const contentWidth = width - padding * 2;

  // Background
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, height);

  // Decorative quotation mark
  ctx.fillStyle = theme.accentColor;
  ctx.globalAlpha = 0.3;
  ctx.font = `bold ${format === 'story' ? 300 : 250}px Georgia, serif`;
  ctx.textAlign = 'left';
  const quoteMarkY = format === 'story' ? height * 0.22 : height * 0.18;
  ctx.fillText('\u201C', padding - 20, quoteMarkY);
  ctx.globalAlpha = 1;

  // Quote text — pick font size based on length
  const quoteLength = data.content.length;
  let fontSize: number;
  if (quoteLength < 50) fontSize = 56;
  else if (quoteLength < 100) fontSize = 48;
  else if (quoteLength < 200) fontSize = 40;
  else fontSize = 34;

  ctx.fillStyle = theme.textColor;
  ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'left';

  const lines = wrapText(ctx, `\u201C${data.content}\u201D`, contentWidth);

  // Vertically center the quote block
  const lineHeight = fontSize * 1.45;
  const totalTextHeight = lines.length * lineHeight;
  const startY = format === 'story'
    ? (height - totalTextHeight) / 2
    : Math.max(height * 0.3, (height - totalTextHeight) / 2 - 40);

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], padding, startY + i * lineHeight);
  }

  // Attribution line
  const attributionY = startY + totalTextHeight + 50;
  ctx.fillStyle = theme.subtextColor;
  ctx.font = `400 30px Inter, system-ui, sans-serif`;

  const formattedDate = formatDate(data.date);
  const attribution = formattedDate
    ? `\u2014 ${data.childName}, ${formattedDate}`
    : `\u2014 ${data.childName}`;
  ctx.fillText(attribution, padding, attributionY);

  // Context (if present)
  if (data.context) {
    ctx.font = `italic 26px Inter, system-ui, sans-serif`;
    ctx.fillStyle = theme.subtextColor;
    ctx.globalAlpha = 0.8;
    const contextLines = wrapText(ctx, data.context, contentWidth);
    for (let i = 0; i < Math.min(contextLines.length, 2); i++) {
      ctx.fillText(contextLines[i], padding, attributionY + 50 + i * 36);
    }
    ctx.globalAlpha = 1;
  }

  // Watermark / branding
  ctx.fillStyle = theme.subtextColor;
  ctx.globalAlpha = 0.5;
  ctx.font = '24px Inter, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('KidQuotes', width - padding, height - 50);
  ctx.globalAlpha = 1;
}

export async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create image'));
      },
      'image/png',
      1.0
    );
  });
}
